/**
 * GenConfigEditor — the world-generation form, RENDERED FROM THE CATALOG rather than authored.
 *
 * The dialog it replaces was 557 lines: twelve `Field<T>` arrays, 74 sliders and 11 pills, listing
 * per subsystem what the generator already knew about itself. Here, adding a knob is one line beside
 * the generator that reads it (shared/src/terrain/nodes/catalog.ts) and this grows for free.
 *
 * THE RENDERER KNOWS NOTHING ABOUT ANY SPECIFIC KIND OR PARAMETER. A `switch` on `ParamSpec.t` is
 * the only dispatch it is allowed. If a case here ever needs `if (kind === 'landform')`, the schema
 * is missing something and the schema is where it goes — otherwise this file starts growing by hand
 * again, which is the whole thing being fixed.
 *
 * Four rules that are decisions rather than styling:
 *
 *  - ONE TREE, ALWAYS WHOLE. Every node's row is on screen at all times; expanding one shows ITS
 *    OWN PARAMETERS and nothing else. Collapsing a node used to take its entire subtree with it,
 *    which meant the shape of the world — what a city contains, that Wild exists at all — was
 *    something you had to go looking for. The structure is the thing worth seeing; the ninety
 *    sliders are what needed hiding.
 *  - NO ADVANCED DISCLOSURE. Parameters used to split into basic and advanced behind a second
 *    collapsed thing inside an already-collapsed node. A node shows all of its own knobs.
 *  - A DISABLED NODE GREYS AND STILL OPENS; IT NEVER DISAPPEARS. The user has to be able to see that
 *    Interiors exists and read why it is unavailable.
 *  - THE ROOT IS NOT A ROW. "World" containing everything is true and useless — it would be one
 *    permanently-open disclosure wrapping the entire form. Its two knobs sit at the top and its
 *    children are the top level.
 */

import { useState } from 'react';
import {
  CATALOG, descriptor, getParam, isEnabled, ROOT_KIND, setEnabled, setParam,
  type KindDescriptor, type ParamSpec, type ParamValue, type WorldGenConfig,
  type CurvePoint, type MaterialName,
} from '@worldify/shared';
import {
  PanelCurve, PanelDisclosure, PanelList, PanelMaterial, PanelNumberField, PanelNumberRow,
  PanelToggle, PanelWeights, PARAM_TITLE_W, ROW_LABEL, SegmentedToggle,
} from './kit';

interface GenConfigEditorProps {
  value: WorldGenConfig;
  onChange: (next: WorldGenConfig) => void;
}

export function GenConfigEditor({ value, onChange }: GenConfigEditorProps) {
  const root = descriptor(ROOT_KIND)!;
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const isOpen = (kind: string) => open[kind] ?? false;
  const toggleOpen = (kind: string, v: boolean) => setOpen((o) => ({ ...o, [kind]: v }));

  return (
    <div className="flex flex-col gap-0.5">
      {/* The root's own knobs, always on screen: they are the world, not one facet of it. */}
      <NodeParams node={root} cfg={value} onChange={onChange} />
      <div className="pt-1 flex flex-col gap-0.5">
        {root.children.map((k) => (
          <NodeBody
            key={k}
            kind={k}
            cfg={value}
            onChange={onChange}
            depth={0}
            isOpen={isOpen}
            toggleOpen={toggleOpen}
          />
        ))}
      </div>
    </div>
  );
}

// ============== Nodes ==============

interface NodeProps {
  kind: string;
  cfg: WorldGenConfig;
  onChange: (next: WorldGenConfig) => void;
  depth: number;
  isOpen: (kind: string) => boolean;
  toggleOpen: (kind: string, v: boolean) => void;
}

/**
 * One node: a header that is always visible, a collapsible body holding ONLY this node's own
 * parameters, and its children rendered after it as siblings — never inside it.
 */
function NodeBody(props: NodeProps) {
  const { kind, cfg, onChange, depth, isOpen, toggleOpen } = props;
  const node = descriptor(kind);
  if (!node) return null;

  const parentOn = parentEnabled(cfg, kind);
  const own = cfg.nodes[kind]?.enabled ?? true;
  // The last node holding a share cannot be unticked: with every share at zero there is nothing left
  // to divide, and a config that says so would be read back as "all of them, equally".
  const lastShare = own && shareOf_isOnlyOne(cfg, node);
  const toggle = node.toggle
    ? {
        value: own,
        onChange: (v: boolean) => onChange(setEnabled(cfg, kind, v)),
        disabled: !parentOn || lastShare,
        title: !parentOn ? `${labelOf(parentOf(kind))} is off`
          : lastShare ? 'The world has to be made of something — leave at least one type on'
          : undefined,
      }
    : undefined;

  return (
    <>
      <PanelDisclosure
        label={node.label}
        depth={depth}
        // A node with no knobs of its own has nothing to open, so it gets no chevron.
        open={node.params.length > 0 && isOpen(kind)}
        onOpenChange={node.params.length > 0 ? (v) => toggleOpen(kind, v) : undefined}
        toggle={toggle}
        summary={shareSummary(cfg, node)}
        inert={!parentOn || !isEnabled(cfg, kind)}
      >
        {node.blurb && <p className="text-[10px] leading-tight text-neutral-400 pb-1">{node.blurb}</p>}
        <NodeParams node={node} cfg={cfg} onChange={onChange} />
      </PanelDisclosure>
      {node.children.map((c) => (
        <NodeBody {...props} key={c} kind={c} depth={depth + 1} />
      ))}
    </>
  );
}

/** A node's own parameters, in declaration order — there is no second tier. */
function NodeParams({ node, cfg, onChange }: {
  node: KindDescriptor; cfg: WorldGenConfig; onChange: (n: WorldGenConfig) => void;
}) {
  return (
    <>
      {node.params.map((p) => (
        <ParamControl key={p.key} kind={node.kind} spec={p} cfg={cfg} onChange={onChange} />
      ))}
    </>
  );
}

// ============== Parameters ==============

/**
 * `compact` is a LIST ROW, where the item's fields share one line with a delete button: no title
 * column, and a number is the typed box without its slider or its range. The dispatch is still on
 * `ParamSpec.t` alone — a list does not learn what a layer contains.
 */
function ParamControl({ kind, spec, cfg, onChange, compact }: {
  kind: string; spec: ParamSpec; cfg: WorldGenConfig; onChange: (n: WorldGenConfig) => void;
  compact?: boolean;
}) {
  const value = getParam(cfg, kind, spec.key);
  const set = (v: ParamValue) => onChange(setParam(cfg, kind, spec.key, v));
  // GREYED, NEVER HIDDEN: a control that vanishes is a control the user hunts for.
  const off = spec.enabledWhen ? !spec.enabledWhen((k, key) => getParam(cfg, k, key)) : false;

  switch (spec.t) {
    case 'number':
      return compact ? (
        <PanelNumberField
          value={typeof value === 'number' ? value : spec.def}
          min={spec.min} max={spec.max} step={spec.step} unit={spec.unit}
          onChange={set} disabled={off} ariaLabel={spec.label}
        />
      ) : (
        <PanelNumberRow
          label={spec.label} desc={spec.desc}
          value={typeof value === 'number' ? value : spec.def}
          min={spec.min} max={spec.max} step={spec.step}
          unit={spec.unit}
          onChange={set} disabled={off}
        />
      );
    case 'bool':
      return (
        <Titled label={spec.label} desc={spec.desc} off={off}>
          <PanelToggle value={value === true} onChange={set} ariaLabel={spec.label} disabled={off} />
        </Titled>
      );
    case 'enum':
      return (
        <Titled label={spec.label} desc={spec.desc} off={off}>
          <SegmentedToggle
            options={spec.options.map((o) => ({ value: o.value, label: o.label }))}
            value={typeof value === 'string' ? value : spec.def}
            onSelect={set}
            ariaLabel={spec.label}
          />
        </Titled>
      );
    case 'multi': {
      const on = (Array.isArray(value) ? value : spec.def) as readonly string[];
      return (
        <Titled label={spec.label} desc={spec.desc} off={off}>
          <div className="flex flex-wrap gap-1">
            {spec.options.map((o) => (
              <Chip
                key={o.value}
                label={o.label}
                active={on.includes(o.value)}
                onClick={() => set(on.includes(o.value)
                  ? on.filter((v) => v !== o.value)
                  : [...on, o.value])}
              />
            ))}
          </div>
        </Titled>
      );
    }
    case 'weights':
      return (
        <Titled label={spec.label} desc={spec.desc} off={off}>
          <PanelWeights
            options={spec.of}
            weights={(Array.isArray(value) ? value : spec.def) as readonly number[]}
            onChange={set}
          />
        </Titled>
      );
    case 'material': {
      const picker = (
        <PanelMaterial
          value={typeof value === 'string' ? value : spec.def}
          onChange={(m: MaterialName) => set(m)}
          ariaLabel={spec.label}
        />
      );
      // Inline, against the same title column the sliders use, so the left edge of every control in
      // the sheet is one line. Stacked, a material sat 92 px left of everything above it.
      return compact ? picker : (
        <Titled label={spec.label} desc={spec.desc} off={off}>{picker}</Titled>
      );
    }
    case 'curve':
      return (
        <Stacked label={spec.label} desc={spec.desc} off={off}>
          <PanelCurve
            points={(Array.isArray(value) ? value : spec.def) as CurvePoint[]}
            onChange={(pts) => set(pts as ParamValue)}
            onReset={() => set(spec.def.map((p) => ({ ...p })) as ParamValue)}
          />
        </Stacked>
      );
    case 'list': {
      const rows = (Array.isArray(value) ? value : spec.def) as Record<string, ParamValue>[];
      const write = (next: Record<string, ParamValue>[]) => set(next);
      const blank = () => Object.fromEntries(spec.item.map((p) => [p.key, p.def as ParamValue]));
      return (
        <Stacked label={spec.label} desc={spec.desc} off={off}>
          <PanelList
            addLabel={`Add ${spec.label.replace(/s$/, '').toLowerCase()}`}
            rows={rows.map((row, i) => ({
              key: String(i),
              // A list row is a config of its own shape, so the SAME ParamControl renders it —
              // reading and writing the row rather than a node. The alternative is a second switch
              // on `ParamSpec.t` handling the two shapes a list happens to contain today, which is
              // exactly where the last one drifted.
              controls: spec.item.map((p) => (
                <div key={p.key} className={p.t === 'material' ? 'flex-1 min-w-0' : 'shrink-0'}>
                  <ParamControl
                    compact
                    kind={ROW_KIND}
                    spec={p}
                    cfg={rowConfig(row, spec.item)}
                    onChange={(n) => write(rows.map((r, j) => (
                      j === i ? { ...r, [p.key]: n.nodes[ROW_KIND].params[p.key] } : r
                    )))}
                  />
                </div>
              )),
            }))}
            onAdd={() => write([...rows, blank()])}
            onRemove={(i) => write(rows.filter((_, j) => j !== i))}
          />
        </Stacked>
      );
    }
  }
}

/** The kind name a list row borrows. Not in the catalog, which is why the row config carries every
 *  value explicitly — `getParam`'s descriptor fallback has nothing to fall back to. */
const ROW_KIND = '__row';

/** A one-node config standing in for one row of a list. */
function rowConfig(row: Record<string, ParamValue>, item: readonly ParamSpec[]): WorldGenConfig {
  const params: Record<string, ParamValue> = {};
  for (const p of item) params[p.key] = row[p.key] ?? (p.def as ParamValue);
  return { version: 0, seed: 0, nodes: { [ROW_KIND]: { enabled: true, params } } };
}

// ============== Small pieces ==============

/** A control on the slider rows' own line: title column left, control right, description under. */
function Titled({ label, desc, off, children }: {
  label: string; desc?: string; off?: boolean; children: React.ReactNode;
}) {
  return (
    <div className={`pb-1.5 ${off ? 'opacity-40 pointer-events-none' : ''}`}>
      <div className="flex items-center gap-1.5">
        <div className={`${ROW_LABEL} !whitespace-normal !overflow-visible ${PARAM_TITLE_W} leading-[1.15]`}>
          {label}
        </div>
        <div className="flex-1 min-w-0">{children}</div>
      </div>
      {desc && <p className="text-[10px] leading-tight text-neutral-400 pl-[98px] pb-0.5">{desc}</p>}
    </div>
  );
}

/** A control that needs the whole width — the elevation curve, and a list of rows. */
function Stacked({ label, desc, off, children }: {
  label: string; desc?: string; off?: boolean; children: React.ReactNode;
}) {
  return (
    <div className={`flex flex-col gap-0.5 pb-1.5 ${off ? 'opacity-40 pointer-events-none' : ''}`}>
      <span className={ROW_LABEL}>{label}</span>
      {children}
      {desc && <p className="text-[10px] leading-tight text-neutral-400">{desc}</p>}
    </div>
  );
}

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 h-7 rounded-full text-[11px] font-bold cursor-pointer transition-all duration-200 ${
        active ? 'bg-[#93CB55] text-[#025C3B]' : 'bg-neutral-100 text-neutral-500 hover:text-neutral-800'
      }`}
    >
      {label}
    </button>
  );
}

// ============== Catalog helpers ==============

function parentOf(kind: string): string | undefined {
  return CATALOG.find((d) => d.children.includes(kind))?.kind;
}

function labelOf(kind: string | undefined): string {
  return (kind && descriptor(kind)?.label) || 'The parent';
}

function parentEnabled(cfg: WorldGenConfig, kind: string): boolean {
  const p = parentOf(kind);
  return p ? isEnabled(cfg, p) : true;
}

/**
 * A SHARE IS RELATIVE, AND THE HEADER IS WHERE THE RESULT GOES.
 *
 * The region types divide the world between them, so 50 says what you SET and not what you get —
 * set Meadow to 100 beside a 30 and a 20 and its actual share is 67%. The slider carries the value,
 * the collapsed row header carries the resulting share, and when the two disagree that difference
 * is the information. A sibling whose tickbox is off contributes nothing, because that is exactly
 * what the resolver does with it.
 *
 * Computed from the schema, so a fourth region type needs no code here. Nothing names `frequency`.
 */
export function siblingShare(cfg: WorldGenConfig, kind: string, key: string): number {
  const siblings = shareSiblings(kind, key);
  const weight = (k: string) => {
    if (cfg.nodes[k]?.enabled === false) return 0;
    const v = getParam(cfg, k, key);
    return typeof v === 'number' ? Math.max(0, v) : 0;
  };
  const total = siblings.reduce((a, k) => a + weight(k), 0);
  return total > 0 ? weight(kind) / total : 0;
}

/** Every node under the same parent that declares this key as a share — this one included. */
function shareSiblings(kind: string, key: string): string[] {
  const parent = parentOf(kind);
  return (parent ? descriptor(parent)?.children ?? [] : [kind])
    .filter((k) => descriptor(k)?.params.some((p) => p.key === key && p.t === 'number' && p.share));
}

/** True when this node holds a share and is the only enabled one that does. */
function shareOf_isOnlyOne(cfg: WorldGenConfig, node: KindDescriptor): boolean {
  const p = node.params.find((q) => q.t === 'number' && q.share);
  if (!p) return false;
  return shareSiblings(node.kind, p.key).every((k) => k === node.kind || cfg.nodes[k]?.enabled === false);
}

/** A collapsed node's right-hand note: its share of the world, when it declares one. */
function shareSummary(cfg: WorldGenConfig, node: KindDescriptor): string | undefined {
  const p = node.params.find((q) => q.t === 'number' && q.share);
  if (!p) return undefined;
  return `${Math.round(siblingShare(cfg, node.kind, p.key) * 100)}%`;
}
